import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useState } from 'react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { apiService } from '../../services/api';
import { getDocTimestampFromLink } from './utils';

interface FinalizeExportStepProps {
  tenderId: string;
  onCompile: () => Promise<void>;
  statusObject?:
    | {
        status: string;
        startedAt?: Date | string;
        lastUpdated?: Date | string;
        error?: string;
        completedAt?: Date | string;
      }
    | undefined;
  fileLink?: string | undefined;
  onDownload: () => void;
  onReset: () => void;
  onUpdateStatus?: (docType: string, status: string) => void;
  sectionsCount?: number;
  reviewedSectionsCount?: number;
  isDownloading?: boolean;
}

const FinalizeExportStep: React.FC<FinalizeExportStepProps> = ({
  tenderId,
  onCompile,
  statusObject,
  fileLink,
  onDownload,
  onReset: _onReset,
  onUpdateStatus,
  sectionsCount = 0,
  isDownloading = false,
}) => {
  const theme = useTheme();
  const { showSnackbar } = useSnackbar();
  const [errorReported, setErrorReported] = useState(false);
  const [errorDismissed, setErrorDismissed] = useState(false);

  const status = statusObject?.status || '';
  const statusLower = status.toLowerCase();
  const isProcessing =
    statusLower === 'processing' ||
    statusLower.includes('generating') ||
    statusLower.includes('compiling');
  const isComplete = statusLower.includes('complete') || statusLower.includes('success');
  const isError =
    (statusLower.includes('error') || statusLower.includes('failed')) &&
    !isComplete &&
    !statusLower.includes('closed'); // Ensure closed errors are hidden

  const handleReportError = async () => {
    try {
      await apiService.reportError({
        tenderId: tenderId || 'unknown',
        error: statusObject?.error || status,
        component: 'Draft Compilation',
      });
      setErrorReported(true);
      setErrorDismissed(true);
      if (onUpdateStatus) {
        onUpdateStatus('compile', 'Error Closed');
      }
      showSnackbar('Error reported. Thank you.', 'success');
    } catch (_e) {
      showSnackbar('Failed to report error.', 'error');
    }
  };

  const getDownloadButtonStyle = (theme: Theme, enabled: boolean) => ({
    backgroundColor: enabled ? theme.palette.bidlogiq.brick : theme.palette.greyLight,
    color: enabled ? theme.palette.bidlogiq.white : theme.palette.greyContrast,
    border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
    fontSize: '1rem',
    fontWeight: 600,
    opacity: enabled ? 1 : 0.7,
    boxShadow: theme.shadows[2],
    height: 'auto',
    minHeight: '36px',
    padding: theme.spacing(0.5, 2),
    '&:hover': {
      backgroundColor: enabled ? theme.palette.bidlogiq.rust : theme.palette.greyLight,
      boxShadow: theme.shadows[4],
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.greyLight,
      color: theme.palette.greyContrast,
      border: `1px solid ${theme.palette.bidlogiq.orange}`,
    },
    width: '100%',
    justifyContent: 'center',
    '& .MuiButton-label': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      lineHeight: 1.2,
    },
  });

  const isCreateDisabled = isProcessing || sectionsCount === 0;

  return (
    <Box>
      {isError && !errorDismissed && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            px: 2,
            borderRadius: 1,
            bgcolor: (theme) => theme.palette.error.light,
            color: (theme) => theme.palette.error.contrastText,
            border: (theme) => `1px solid ${theme.palette.error.main}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
            <ErrorIcon fontSize="medium" />
            <Typography variant="body1" sx={{ fontWeight: 500 }} noWrap>
              <span style={{ fontWeight: 'bold' }}>Compilation Failed:</span>{' '}
              {statusObject?.error || status}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleReportError}
              disabled={errorReported}
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: 'white',
                color: 'error.main',
                borderColor: 'error.main',
                py: 0.5,
                px: 2,
                minHeight: '32px',
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {errorReported ? 'Report Sent' : 'Notify Admin'}
            </Button>
            <IconButton
              size="small"
              onClick={() => {
                setErrorDismissed(true);
                if (onUpdateStatus) {
                  onUpdateStatus('compile', 'Error Closed');
                }
              }}
              sx={{ color: 'inherit', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ pr: { md: 2 } }}>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: theme.palette.text.secondary }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Merge all sections</strong> above into a single DOCX file.
                </Typography>
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Fill forms or generate additional documents</strong> in Step 12.
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                  <strong>Create tailored CVs</strong> for your proposed team in Step 13.
                </Typography>
              </li>
            </ul>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Pre-generation checks */}
            {/* <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1rem' }}>
                Pre-generation checks
              </Typography>
              {preGenChecks.map((check) => (
                <Box key={check.label} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {check.passed ? (
                      <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                    ) : (
                      <ErrorIcon sx={{ fontSize: 20, color: 'warning.main' }} />
                    )}
                    <Typography
                      variant="body1"
                      color={check.passed ? 'text.primary' : 'text.secondary'}
                      sx={{ fontSize: '1rem' }}
                    >
                      {check.label}
                    </Typography>
                  </Box>
                  {!check.passed && (
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 4,
                        fontSize: '0.875rem',
                        color:
                          theme.palette.mode === 'dark'
                            ? theme.palette.bidlogiq.camel
                            : 'error.main',
                      }}
                    >
                      {check.failureMessage}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box> */}

            {/* LLM Check Button
            <Button
              variant="outlined"
              size="large"
              disabled={true}
              startIcon={<AutoAwesomeIcon />}
              sx={{
                borderColor: theme.palette.bidlogiq.brick,
                color: theme.palette.bidlogiq.brick,
                fontWeight: 'bold',
                '&:hover': {
                  borderColor: theme.palette.bidlogiq.rust,
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  borderColor: theme.palette.greyLight,
                  color: theme.palette.greyContrast,
                },
              }}
            >
              Check for internal inconsistencies (coming soon!)
            </Button> */}

            {/* Create Button */}
            <Tooltip
              title={
                sectionsCount === 0
                  ? 'No sections available to compile. Please generate a response first.'
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onCompile}
                  disabled={isCreateDisabled}
                  startIcon={
                    isProcessing ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
                  }
                  sx={{
                    backgroundColor: !isCreateDisabled
                      ? theme.palette.greyContrast
                      : theme.palette.greyLight,
                    color: !isCreateDisabled ? theme.palette.greyLight : theme.palette.greyContrast,
                    border: !isCreateDisabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
                    fontSize: '1rem',
                    fontWeight: 600,
                    opacity: !isCreateDisabled ? 1 : 0.7,
                    boxShadow: theme.shadows[2],
                    height: 'auto',
                    minHeight: '36px',
                    padding: theme.spacing(0.5, 2),
                    width: '100%',
                    '&:hover': {
                      backgroundColor: !isCreateDisabled
                        ? theme.palette.bidlogiq.brick || theme.palette.primary.main
                        : theme.palette.greyLight,
                      boxShadow: theme.shadows[4],
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.greyLight,
                      color: theme.palette.greyContrast,
                      border: `1px solid ${theme.palette.bidlogiq.orange}`,
                    },
                  }}
                >
                  {isProcessing ? 'Creating...' : 'Create DOCX'}
                </Button>
              </span>
            </Tooltip>

            {/* Download Button */}
            <Button
              startIcon={
                isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />
              }
              onClick={onDownload}
              disabled={!fileLink || isProcessing || isDownloading}
              sx={getDownloadButtonStyle(theme, !!fileLink && !isProcessing && !isDownloading)}
            >
              <Box
                sx={{
                  lineHeight: 1.2,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: '1rem',
                }}
              >
                Download draft proposal
                {(fileLink || statusObject?.completedAt) &&
                  (() => {
                    const ts = statusObject?.lastUpdated || statusObject?.completedAt;
                    return (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          fontWeight: 400,
                          opacity: 0.9,
                          textTransform: 'none',
                        }}
                      >
                        {`, ${getDocTimestampFromLink(fileLink, ts ? { lastUpdated: ts } : undefined)}`}
                      </Box>
                    );
                  })()}
                <Box component="span" sx={{ textTransform: 'none' }}>
                  {' (docx)'}
                </Box>
              </Box>
            </Button>

            {/* Reset Button */}
            {/* {(status !== 'idle' && status !== '') && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <Button
                                    variant="text"
                                    onClick={onReset}
                                    startIcon={<ReplayIcon />}
                                    sx={{ color: 'text.secondary' }}
                                >
                                    Reset Status
                                </Button>
                            </Box>
                        )} */}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinalizeExportStep;
